import { CardReportRequest } from 'core/models/CardReportRequest';
import { SearchCardMovementsRequest } from 'core/models/SearchCardMovementsRequest';
import { useFetcherService, FetcherService, URLParamsBuilder } from 'shared/http';
import { Pageable } from 'shared/table/models/Pageable';
import { CardMovementsReport } from 'core/models/CardMovementsReport';
import { SearchAccountsTotalsRequest } from 'core/cuenta/models/SearchAccountTotalsRequest';
import { AccountsTotalsData } from 'core/cuenta/models/AccountsTotalsData';
import { AccountReportRequest } from 'core/models/AccountReportRequest';
import { AccountReport } from 'core/models/AccountReport';
import { SearchAccountMovementsRequest } from 'core/models/SearchAccountMovementsRequest';
import { MovimientoCuenta } from 'core/models/data/MovimientoCuenta';
import { CardsTotalsByAccountData } from 'core/tarjeta/models/CardsTotalsByAccountData';
import { SearchCardTotalsRequest } from 'core/tarjeta/models/requests/SearchCardTotalsRequest';
import { AccountTotals } from 'core/cuenta/models/AccountTotals';
import { AccountAuthorizations } from 'core/cuenta/models/AccountAuthorizations';
import { CentroCostoSelectOption } from 'core/centrocosto/models/CentroCostoSelectOption';
import { SearchCostCentreTotalsRequest } from 'core/centrocosto/models/requests/SearchCostCentreTotalsRequest';
import { CentroCostoTotalsReport } from 'core/centrocosto/models/CentroCostoTotalsReport/CentroCostoTotalsReport';
import { ConsolidatedReport } from 'core/empresa/models/ConsolidatedReport';
import { CardsTotals } from 'core/tarjeta/models/CardsTotals';
import { CardReport } from 'core/tarjeta/models/CardReport';
import { Movimiento } from 'core/tarjeta/models/Movimiento';
import { CentroCostoLista } from 'core/centrocosto/models/CentroCostoLista/CentroCostoLista';
import { RefundRequest } from 'core/cuenta/models/requests/RefundRequest';
import { SearchTransfersRequest } from 'core/models/SearchTransfersRequest';
import { FileResponse } from 'core/models/data/FileResponse';
import { RefundData } from 'core/cuenta/models/RefundData';
import { Transferencia } from 'core/cuenta/models/Transferencia';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { DistribucionDeFondosRequest } from 'core/cuenta/models/requests/DistribucionDeFondosRequest';
import { FundsDistributionData } from 'core/cuenta/models/FundsDistributionData';
import { CentroCostoRequest } from 'core/cuenta/models/requests/CrearCentroCostoRequest';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { AccountBalance } from '../cuenta/models/AccountBalance';
import { Tarjeta } from '../tarjeta/models/Tarjeta';
import { SearchCentroCostoRequest } from 'core/centrocosto/models/SearchCentroCostoRequest';
import { SearchTarjetaRequest } from 'core/tarjeta/models/SearchTarjetaRequest';
import { SearchCuentasRequest } from 'core/cuenta/models/SearchCuentasRequests';

class CoreService extends FetcherService {
  async getTarjeta(request: CardReportRequest) {
    return this.get<CardReport>(`tarjetas/${request.cardNumber}/reporte`, request);
  }

  async getAllTarjetas(params?: SearchTarjetaRequest): Promise<Pageable<Tarjeta>> {
    const url = new URLParamsBuilder('tarjetas')
      .addQueryParams({
        numeroCuentaOrigen: params?.numeroCuentaOrigen,
        numeroCuenta: params?.numeroCuenta,
        numeroTarjeta: params?.numeroTarjeta,
        centroCostoAsociadoId: params?.centroCostoAsociadoId,
        denominacionTarjeta: params?.denominacionTarjeta,
        page: params?.page,
        size: params?.size,
        cuentasHuerfanas: params?.cuentasHuerfanas,
        conSaldo: params?.conSaldo,
      })
      .build();
    const response = await this.get<Pageable<any>>(url);
    const pageableResponse = response.body;

    const mappedContent: Tarjeta[] = pageableResponse.content.map((apiCard: any) => {
      return new Tarjeta({
        id: apiCard.id,
        numero: apiCard.numeroTarjeta,
        denominacion: apiCard.denominacionTarjeta,
        numeroCuenta: apiCard.numeroCuenta,
        centroCostoAsociado: apiCard.centroCostoAsociado,
      });
    });

    return {
      ...pageableResponse,
      content: mappedContent,
    };
  }

  async getAccountData(nroCuenta: string, request: AccountReportRequest) {
    return this.get<AccountReport>(`cuentas/${nroCuenta}/reporte`, request);
  }
  async getAccountSaldo(nroCuenta: string) {
    return this.get<AccountBalance>(`cuentas/${nroCuenta}`);
  }

  async getCardReportByCategory(request: CardReportRequest) {
    return this.get<CardMovementsReport>(
      `tarjetas/${request.cardNumber}/rubros`,
      request,
    );
  }
  async getCardAuthorizations(searchCardMovementsRequest: SearchCardMovementsRequest) {
    return this.get<CardReport>(
      `tarjetas/${searchCardMovementsRequest.numeroTarjeta}/autorizaciones`,
      {
        anio: searchCardMovementsRequest.anio,
        mes: searchCardMovementsRequest.mes,
      },
    );
  }
  async getCardMovements(searchCardMovementsRequest: SearchCardMovementsRequest) {
    return this.get<Pageable<Movimiento>>(
      `tarjetas/${searchCardMovementsRequest.numeroTarjeta}/movimientos`,
      {
        anio: searchCardMovementsRequest.anio,
        mes: searchCardMovementsRequest.mes,
        rubroId: searchCardMovementsRequest.rubroId,
        page: searchCardMovementsRequest.page,
        size: searchCardMovementsRequest.size,
      },
    );
  }

  async searchAccountsTotalsReport(
    request?: SearchAccountsTotalsRequest,
  ): Promise<AccountsTotalsData> {
    return this.get<{
      denominacionEmpresa: string;
      totalesPorCuenta: Pageable<AccountTotals>;
      totalesEmpresa: AccountTotals;
    }>('cuentas/totales/reporte', {
      anio: request?.anio,
      mes: request?.mes,
      page: request?.page,
      size: request?.size,
    }).then((r) => {
      return new AccountsTotalsData(r.body);
    });
  }

  async searchAccountsTotals(request?: SearchAccountsTotalsRequest) {
    return this.get<Pageable<AccountTotals>>('cuentas/totales', {
      anio: request?.anio,
      mes: request?.mes,
      page: request?.page,
      size: request?.size,
    });
  }

  async searchCardTotalsReport(request: SearchCardTotalsRequest) {
    return this.get<CardsTotalsByAccountData>(
      `cuentas/${request.numeroCuenta}/totales/reporte`,
      {
        anio: request?.anio,
        mes: request?.mes,
        page: request?.page,
        size: request?.size,
      },
    );
  }

  async searchCardTotals(request: SearchCardTotalsRequest) {
    return this.get<Pageable<CardsTotals>>(`tarjetas/totales`, {
      nroCuenta: request?.numeroCuenta,
      anio: request?.anio,
      mes: request?.mes,
      page: request?.page,
      size: request?.size,
    });
  }

  async getVisibleAccounts(request: SearchCuentasRequest): Promise<Cuenta[]> {
    return this.get<Cuenta[]>('cuentas', request).then((r) =>
      r.body.map((cuenta) => new Cuenta(cuenta)),
    );
  }

  async getUnnasignedAccounts(): Promise<Cuenta[]> {
    return this.get<Cuenta[]>(`cuentas/sinasignar`).then((r) =>
      r.body.map((cuenta) => new Cuenta(cuenta)),
    );
  }

  async getAccountMovements(
    searchAccountMovementsRequest: SearchAccountMovementsRequest,
  ) {
    return this.get<Pageable<MovimientoCuenta>>(
      `cuentas/${searchAccountMovementsRequest.nroCuenta}/movimientos`,
      {
        anio: searchAccountMovementsRequest.anio,
        mes: searchAccountMovementsRequest.mes,
        page: searchAccountMovementsRequest.page,
        size: searchAccountMovementsRequest.size,
        nroCuenta: searchAccountMovementsRequest.nroCuenta,
      },
    );
  }
  async getAccountAuthorizations(request: SearchAccountMovementsRequest) {
    return this.get<AccountAuthorizations>(
      `cuentas/${request.nroCuenta}/autorizaciones`,
      {
        anio: request.anio,
        mes: request.mes,
        page: request.page,
        size: request.size,
        nroCuenta: request.nroCuenta,
      },
    );
  }
  async getAccountLast12Months(): Promise<ConsolidatedReport> {
    return this.get<ConsolidatedReport>(`cuentas/totales/resumen`).then((r) => r.body);
  }

  async createCostCentre(data: CentroCostoRequest) {
    return this.post<void>('centroscosto', data);
  }

  async getCostCentres(
    request?: SearchCentroCostoRequest,
  ): Promise<Pageable<CentroCosto>> {
    const response = await this.get<Pageable<CentroCosto>>(`centroscosto`, {
      numeroCuentaOrigen: request?.numeroCuentaOrigen,
      numeroCuenta: request?.numeroCuenta,
      denominacion: request?.denominacion,
      centroCostoPadreId: request?.centroCostoPadreId,
      cuentasHuerfanas: request?.cuentasHuerfanas,
      conSaldo: request?.conSaldo ?? null,
      page: request?.page,
      size: request?.size,
    });
    return response.body;
  }

  async getCostCentreLast12Months(): Promise<ConsolidatedReport> {
    return this.get<ConsolidatedReport>(`centroscosto/totales/resumen`).then(
      (r) => r.body,
    );
  }

  async getCostCentre(id: string) {
    return this.get<CentroCosto>(`centroscosto/${id}`).then((r) => r.body);
  }

  async updateCostCentre(request: CentroCostoRequest) {
    return this.put<void>(`centroscosto/${request.id}`, request);
  }

  async moveCostCentre(id: number, request: { centroCostoPadreId: number }) {
    return this.put<void>(`centroscosto/mover/${id}`, request);
  }

  async deleteCostCentre(idCostCentre: number) {
    return this.del<void>(`centroscosto/${idCostCentre}`);
  }

  async getCostCentreList() {
    return this.get<CentroCostoSelectOption[]>(`centroscosto/listado`);
  }

  async getCostCentreListMassDistribution() {
    return this.get<CentroCostoLista[]>(`centroscosto/listado/distribucion`);
  }

  async getMassDistributionTemplate(request: any) {
    return this.get<FileResponse>(`centroscosto/listado/template`, request, true);
  }

  async validateMassDistributionTemplate(request: any) {
    return this.post<any>(`centroscosto/listado/template/validate`, request, true);
  }

  async getCostCentreTotals(
    request: SearchCostCentreTotalsRequest,
  ): Promise<CentroCostoTotalsReport> {
    return this.get<CentroCostoTotalsReport>(
      `centroscosto/${request.costCentreId}/totales`,
      request,
    ).then((r) => r.body);
  }

  async saveFundsDistribution(
    request: DistribucionDeFondosRequest,
    numeroCuenta: string,
  ): Promise<Transferencia[]> {
    request.numeroCuenta = numeroCuenta;
    return this.post<Transferencia[]>(`cuentas/transferencias`, request).then(
      (r) => r.body,
    );
  }

  async getFundsDistributionData(cuentaOrigen?: string): Promise<FundsDistributionData> {
    const url = cuentaOrigen ? `?cuentaOrigen=${cuentaOrigen}` : '';
    return this.get<FundsDistributionData>(`cuentas/distribucion${url}`).then((r) => ({
      destinos: r.body?.destinos.map((cuenta) => new Cuenta(cuenta)),
      origen: new Cuenta(r.body?.origen),
    }));
  }

  async getCostCentreRefund(): Promise<RefundData> {
    return this.get<RefundData>(`cuentas/devolucion`).then((r) => r.body);
  }

  async saveCostCentreRefund(request: RefundRequest): Promise<Transferencia> {
    return this.post<Transferencia>(`cuentas/transferencias/devolucion`, request).then(
      (r) => r.body,
    );
  }

  async getCostCentresChildren(): Promise<CentroCosto[]> {
    return this.get<CentroCosto[]>('centroscosto/hijos').then((response) => {
      if (!response.body || !Array.isArray(response.body)) {
        return [];
      }
      return response.body;
    });
  }

  async associateCardToCostCenter(numeroCuentaTarjeta: string, idCentroCosto: number) {
    return this.post<void>(`tarjetas/${numeroCuentaTarjeta}/asociar/${idCentroCosto}`);
  }

  async disassociateCard(idTarjeta: number) {
    return this.patch<void>(`tarjetas/${idTarjeta}/desasociar`);
  }

  async findTransferencias(
    request: SearchTransfersRequest,
  ): Promise<Pageable<Transferencia>> {
    return this.get<Pageable<Transferencia>>(`cuentas/transferencias`, {
      cuentaOrigen: request.cuentaOrigen,
      cuentaDestino: request.cuentaDestino,
      from: request.getFromDateString(),
      to: request.getToDateString(),
      page: request.page,
      size: request.size,
      estado: request.estado,
      username: request.username,
    }).then((r) => r.body);
  }

  async downloadTransferComprobante(transferId: number) {
    return this.get<{ file: Blob; name: string }>(
      `cuentas/transferencias/${transferId}/comprobante`,
      undefined,
      true, // <- archivo
    ).then((r) => r.body);
  }

}

const coreService = new CoreService();

export function useCoreService() {
  const { registerFetcherService } = useFetcherService();

  registerFetcherService(coreService);

  return coreService;
}

export type ReportFormat = 'PDF' | 'XLS';
export interface GenerateReportRequest {
  id: number;
  formato: ReportFormat;
  fechaDesde: string;
  fechaHasta: string;
}

export const useReporteService = () => {
  const coreService = useCoreService();

  const generateMovimientosPorTarjetaReport = async (request: GenerateReportRequest) => {
    // Reutilizamos el método `get` del servicio principal, que ya maneja la autenticación y el refresco de tokens.
    // El tercer parámetro `true` indica que esperamos un archivo como respuesta.
    const response = await coreService.get<{ file: Blob; name: string }>(
      `reportes/movimientos-por-tarjeta`,
      request,
      true,
    );

    // Retornamos el objeto con el archivo y su nombre
    return response.body;
  };

  return {
    generateMovimientosPorTarjetaReport,
  };
};


